import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Center,
  VStack,
  Text,
  Menu,
  MenuItem,
  MenuList,
  MenuButton,
} from '@chakra-ui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import PlusIcon from '../../../assets/icons/plus.svg';
import {
  CenteredSpinner,
  FilterBadges,
  FilterDrawer,
  Paginator,
  RoundedBadge,
  TableHeader,
} from '../../../components';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';

import {
  useGetAllProjectsQuery,
  useDeleteProjectMutation,
} from '../../../services';

import { NewProjectModal, DeleteProjectModal } from '../components';

import {
  projectsInitialFilters,
  projectsFilters,
  projectsSorts,
  projectStatusTypeMapper,
} from '../utils';
import { ProjectFilters, ProjectType } from '../../../models';
import { extractName, formatDate } from '../../../utils';

export const Projects: FC = () => {
  const [selectedProject, setSelectedProject] = useState<number | undefined>(
    undefined
  );
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(projectsInitialFilters);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onOpenFilterDrawer,
    onClose: onCloseFilterDrawer,
  } = useDisclosure();

  const { type } = useParams();
  const [searchParams] = useSearchParams();

  if (type === 'single-phase') {
    projectsInitialFilters.projectType = ProjectType.SINGLE;
  } else if (type === 'multiple-phase') {
    projectsInitialFilters.projectType = ProjectType.MULTI;
  } else projectsInitialFilters.projectType = undefined;

  const {
    isOpen: isDeleteProjectModalOpen,
    onOpen: onOpenDeleteProjectModal,
    onClose: onCloseDeleteProjectModal,
  } = useDisclosure();

  const {
    isOpen: isNewProjectModalOpen,
    onOpen: onOpenNewProjectModal,
    onClose: onCloseNewProjectModal,
  } = useDisclosure();

  const navigate = useNavigate();

  const { data, isLoading, isFetching, refetch } = useGetAllProjectsQuery({
    page,
    projectIdentifier: debouncedSearchTerm,
    ...filters,
  });

  const [deleteProject] = useDeleteProjectMutation();

  const handleFilterDrawerClose = (filterValues: ProjectFilters) => {
    onCloseFilterDrawer();
    if (!filterValues) return;

    let filterName: keyof typeof filterValues;

    for (filterName in filterValues) {
      if (filterValues[filterName] === '') delete filterValues[filterName];
    }

    setFilters(filters => ({ ...filters, ...filterValues }));

    void refetch();
  };

  const handleRemoveFilter = (filterKey: keyof typeof filters) => {
    setFilters({ ...filters, [filterKey]: undefined });

    void refetch();
  };

  return (
    <Box h="full">
      {isFilterDrawerOpen && (
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={handleFilterDrawerClose}
          filters={projectsFilters}
          sorts={projectsSorts}
        />
      )}

      {isDeleteProjectModalOpen && (
        <DeleteProjectModal
          isOpen={isDeleteProjectModalOpen}
          onClose={isDeleted => {
            if (isDeleted && selectedProject)
              void deleteProject({ projectId: selectedProject });
            onCloseDeleteProjectModal();
          }}
        />
      )}

      {isNewProjectModalOpen && (
        <NewProjectModal
          isOpen={isNewProjectModalOpen}
          onClose={onCloseNewProjectModal}
        />
      )}

      <VStack align="stretch">
        <HStack justify="space-between" mt="4" align="start">
          <HStack align="baseline">
            <Heading
              color="custom_blue.1"
              fontSize="2xl"
              textTransform="capitalize"
            >
              {type !== 'all' ? type?.split('-').join(' ') : ''} Projects
            </Heading>
            <Box color="custom_gray.1" fontSize="sm">
              (
              {(data ? String(data?.data.totalElements) : '0').padStart(2, '0')}{' '}
              Projects)
            </Box>
          </HStack>

          <Button
            onClick={onOpenNewProjectModal}
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
            fontWeight="semibold"
            fontSize="sm"
          >
            <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
            Create New Project
          </Button>
        </HStack>

        <VStack
          align="stretch"
          bg="white"
          border="1px"
          borderColor="custom_gray.3"
          my="4"
          p="3"
          gap="3"
          borderRadius="md"
          minH="80vh"
          h="auto"
        >
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search by name / code"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpenFilterDrawer}
            >
              Filters
            </Button>
          </HStack>

          <FilterBadges onRemoveFilter={handleRemoveFilter} filters={filters} />

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <TableContainer
              h="full"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minH="70vh"
            >
              <Table variant="simple">
                <Thead bg="custom_gray.2">
                  <Tr>
                    <TableHeader title="Project Name" />
                    <TableHeader title="Status" />
                    <TableHeader title="Start Date" />
                    <TableHeader title="End Date" />
                    <TableHeader title="Members" />
                    {/* <TableHeader title="Progress" /> */}
                    <TableHeader title="Actions" />
                  </Tr>
                </Thead>
                <Tbody>
                  {data?.data.content.map(
                    ({
                      id,
                      projectName,
                      projectCode,
                      calculatedEndDate,
                      // calculatedProgress,
                      calculatedStartDate,
                      calculatedTeamMembers,
                      projectThumbnailImageURL,
                      projectStatusEnum,
                      projectTypeEnum,
                    }) => (
                      <Tr key={id}>
                        <Td
                          border="none"
                          cursor="pointer"
                          onClick={() =>
                            navigate(
                              `/projects/${
                                projectTypeEnum === 'SINGLE'
                                  ? 'single-phase'
                                  : 'multiple-phase'
                              }/${id}/project-details`
                            )
                          }
                        >
                          <HStack>
                            <Box
                              w="10"
                              h="10"
                              bg="custom_blue.1"
                              borderRadius="full"
                            >
                              {projectThumbnailImageURL ? (
                                <Image
                                  src={projectThumbnailImageURL}
                                  borderRadius="full"
                                />
                              ) : (
                                <Center
                                  h="full"
                                  color="white"
                                  textTransform="capitalize"
                                >
                                  {extractName(projectName)}
                                </Center>
                              )}
                            </Box>
                            <VStack align="flex-start" gap=".5">
                              <Text
                                color="custom_blue.1"
                                fontWeight="medium"
                                fontSize="sm"
                              >
                                {projectName}
                              </Text>
                              <Text color="custom_blue.1" fontSize="sm">
                                {projectCode}
                              </Text>
                            </VStack>
                          </HStack>
                        </Td>

                        <Td border="none">
                          <RoundedBadge
                            colorSchema={projectStatusEnum}
                            textMapper={projectStatusTypeMapper}
                          >
                            {projectStatusEnum}
                          </RoundedBadge>
                        </Td>
                        <Td color="custom_blue.1" fontSize="md" border="none">
                          {formatDate(calculatedStartDate)}
                        </Td>
                        <Td color="custom_blue.1" fontSize="md" border="none">
                          {formatDate(calculatedEndDate)}
                        </Td>
                        <Td color="custom_blue.1" fontSize="sm" border="none">
                          {calculatedTeamMembers}
                        </Td>
                        {/* TODO: reuse after implementation */}
                        {/* <Td border="none">
                          <Box w="48">
                            <CustomProgress value={calculatedProgress} />
                          </Box>
                        </Td> */}
                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<DotsIcon />}
                              aria-label="Actions role"
                              bg="transparent"
                            />
                            <MenuList>
                              <MenuItem
                                onClick={() =>
                                  navigate(
                                    `/projects/${
                                      projectTypeEnum === 'SINGLE'
                                        ? 'single-phase'
                                        : 'multiple-phase'
                                    }/${id}/project-details`
                                  )
                                }
                              >
                                View
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  navigate(
                                    `/projects/${
                                      projectTypeEnum === 'SINGLE'
                                        ? 'single-phase'
                                        : 'multiple-phase'
                                    }/${id}`
                                  )
                                }
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  onOpenDeleteProjectModal();
                                  setSelectedProject(id);
                                }}
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    )
                  )}
                </Tbody>
              </Table>

              <Paginator
                pageCount={Number(data?.data.totalPages)}
                onPageChange={page => setPage(page)}
              />
            </TableContainer>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};
